import { useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ENTITY_ICONS, orgFunctionKindIcon } from '@/constants/entityIcons';
import { OrgMemberStatusActions } from './OrgMemberStatusActions';
import { FieldSeparator, Badge, useClearOnChange } from '@bliks-io/ui';
import {
  FormInput,
  FormDepartmentSelect,
  FormMultiSelect,
  FormSelect,
  FormCheckbox,
  FormLocationSelect
} from '@/components/form/rhf';
import { useOrgFunctions } from '@/api/queries/useOrgFunctions';
import {
  AddOrgMemberRequestSchema,
  type AddOrgMemberRequest,
  type OrgFunction,
  type UpdateOrgMemberRequest,
  type User
} from '@bliks-io/contracts';
import { FormDialog } from '@/components/dialogs/FormDialog';
import {
  secondaryRoleOptionsExcludingMainRole,
  secondaryRolesWithoutMainRole
} from '../utils/roleSlots';
import {
  getUserStatusLabel,
  getUserStatusColorScheme,
  getUserTypeOptions,
  getUserClassificationOptions,
  externalDefaultUserTypes
} from '@/lib/user-utils';
import { useEmailChangeWarning, useSessionTenantType } from '@/hooks';

interface OrgMemberFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: AddOrgMemberRequest | UpdateOrgMemberRequest) => unknown;
  member?: User;
  onSendInvitation?: (memberId: string) => void | Promise<void>;
  onSuspend?: (memberId: string) => void | Promise<void>;
  onUnsuspend?: (memberId: string) => void | Promise<void>;
  isSendingInvitation?: boolean;
  isSuspending?: boolean;
  isUnsuspending?: boolean;
  /**
   * If provided, the department field will be locked to this department ID
   * and cannot be changed.
   */
  lockedDepartmentId?: string;
  /**
   * If false, the User Types field will be hidden from the form.
   * Defaults to true. Only system admins can manage user types.
   */
  showUserTypes?: boolean;
}

// The Standort combobox clears to '', which the request refuses: the submit
// maps it to undefined on create and to null (clear) on edit.
const OrgMemberFormSchema = AddOrgMemberRequestSchema.safeExtend({
  location: z.string().trim().optional()
});

function toOrgFunctionOption(orgFunction: OrgFunction) {
  // Per-kind entity icon matches the other OrgFunction pickers; kept beside
  // the string label so search still matches on the name.
  return {
    value: orgFunction._id,
    label: orgFunction.name,
    icon: orgFunctionKindIcon(orgFunction.kind)
  };
}

/**
 * Form dialog for creating/editing organization members
 */
export function OrgMemberFormDialog({
  open,
  onOpenChange,
  onSubmit,
  member,
  onSendInvitation,
  onSuspend,
  onUnsuspend,
  isSendingInvitation = false,
  isSuspending = false,
  isUnsuspending = false,
  lockedDepartmentId,
  showUserTypes = true
}: OrgMemberFormDialogProps) {
  const { t } = useTranslation();
  const tenantType = useSessionTenantType();
  const isEditing = !!member;

  const memberClassification = member?.classification || 'internal';
  const memberIsExternal = memberClassification === 'external';

  const defaultValues: AddOrgMemberRequest = {
    firstName: member?.firstName || '',
    lastName: member?.lastName || '',
    email: member?.email || '',
    department: memberIsExternal ? undefined : lockedDepartmentId || member?.department?._id || '',
    mainRole: member?.mainRole?._id || '',
    secondaryRoles: member?.secondaryRoles?.map((orgFunction) => orgFunction._id) || [],
    userTypes: member?.userTypes || [],
    sendInvitation: false,
    classification: memberClassification,
    externalOrganization: member?.externalOrganization || '',
    location: member?.location || ''
  };

  // A member may already hold a role this tenant's branch no longer offers;
  // re-admitting it keeps that member editable.
  const userTypeOptions = getUserTypeOptions(t, tenantType, member?.userTypes);
  const classificationOptions = getUserClassificationOptions(t);

  // Cleared selects hold '' in form state, which the wire contracts reject
  // ('' is neither an id nor the clear convention). On create the field is
  // simply omitted. On edit, FormDialog delivers only the dirty subset: a
  // cleared mainRole becomes null (the server $unsets the Stelle); department
  // has no clear convention on PATCH — it only empties when switching the
  // member to external, where it must be absent entirely.
  const handleSubmit = (data: AddOrgMemberRequest) => {
    if (!isEditing) {
      return onSubmit({
        ...data,
        mainRole: data.mainRole || undefined,
        // Backstop for the slot invariant the pickers already enforce: the
        // same OrgFunction must never be Hauptfunktion AND Zusatzfunktion.
        secondaryRoles: secondaryRolesWithoutMainRole(data.secondaryRoles, data.mainRole),
        department: data.department || undefined,
        location: data.location || undefined
      });
    }
    const update: UpdateOrgMemberRequest = { ...data };
    if ('mainRole' in data) update.mainRole = data.mainRole || null;
    if ('location' in data) update.location = data.location || null;
    if ('secondaryRoles' in data) {
      const effectiveMainRole =
        'mainRole' in data ? data.mainRole || undefined : member?.mainRole?._id;
      update.secondaryRoles = secondaryRolesWithoutMainRole(data.secondaryRoles, effectiveMainRole);
    }
    if ('department' in data && !data.department) delete update.department;
    return onSubmit(update);
  };

  return (
    <FormDialog<AddOrgMemberRequest>
      open={open}
      onOpenChange={onOpenChange}
      title={
        isEditing ? t('organization.members.editMember') : t('organization.members.createMember')
      }
      description={
        isEditing
          ? t('organization.members.editMemberDescription')
          : t('organization.members.createMemberDescription')
      }
      icon={ENTITY_ICONS.orgMember}
      schema={OrgMemberFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isEditing={isEditing}
    >
      {({ form }) => {
        const classification = form.watch('classification');
        const isExternal = classification === 'external';
        return (
          <OrgMemberFormBody
            form={form}
            isEditing={isEditing}
            isExternal={isExternal}
            memberClassification={memberClassification}
            isOpen={open}
            member={member}
            lockedDepartmentId={lockedDepartmentId}
            showUserTypes={showUserTypes}
            classificationOptions={classificationOptions}
            userTypeOptions={userTypeOptions}
            onSendInvitation={onSendInvitation}
            onSuspend={onSuspend}
            onUnsuspend={onUnsuspend}
            isSendingInvitation={isSendingInvitation}
            isSuspending={isSuspending}
            isUnsuspending={isUnsuspending}
          />
        );
      }}
    </FormDialog>
  );
}

interface OrgMemberFormBodyProps {
  form: UseFormReturn<AddOrgMemberRequest>;
  isEditing: boolean;
  isExternal: boolean;
  memberClassification: 'internal' | 'external';
  isOpen: boolean;
  member?: User;
  lockedDepartmentId?: string;
  showUserTypes: boolean;
  classificationOptions: { label: string; value: string }[];
  userTypeOptions: { label: string; value: string }[];
  onSendInvitation?: (memberId: string) => void | Promise<void>;
  onSuspend?: (memberId: string) => void | Promise<void>;
  onUnsuspend?: (memberId: string) => void | Promise<void>;
  isSendingInvitation: boolean;
  isSuspending: boolean;
  isUnsuspending: boolean;
}

function OrgMemberFormBody({
  form,
  isEditing,
  isExternal,
  memberClassification,
  isOpen,
  member,
  lockedDepartmentId,
  showUserTypes,
  classificationOptions,
  userTypeOptions,
  onSendInvitation,
  onSuspend,
  onUnsuspend,
  isSendingInvitation,
  isSuspending,
  isUnsuspending
}: OrgMemberFormBodyProps) {
  const { t } = useTranslation();

  // One OrgFunctions fetch feeds both pickers, narrowed to role-kind: mainRole
  // offers the Stelle candidates, and the secondary-functions picker must not
  // offer team-kind OrgFunctions — team membership is managed from the
  // Funktion's member list, not the person editor. Stored team-kind (and
  // tombstoned) links survive a save untouched: they round-trip invisibly
  // through form state and the server only validates newly added ids.
  const { data: orgFunctions, isLoading: orgFunctionsLoading } = useOrgFunctions();

  const roleOrgFunctionOptions = useMemo(
    () => (orgFunctions ?? []).filter((f) => f.kind === 'role').map(toOrgFunctionOption),
    [orgFunctions]
  );

  // Slot invariant: the selected Hauptfunktion never appears as a
  // Zusatzfunktion candidate, and picking a new Hauptfunktion drops it from
  // the selected Zusatzfunktionen (derived options + change handler — the
  // submit additionally sanitizes as a backstop).
  const mainRole = form.watch('mainRole');
  const secondaryRoleOptions = useMemo(
    () => secondaryRoleOptionsExcludingMainRole(roleOrgFunctionOptions, mainRole || undefined),
    [roleOrgFunctionOptions, mainRole]
  );
  const handleMainRoleChange = (nextMainRole: string | undefined) => {
    const current = form.getValues('secondaryRoles') ?? [];
    const cleaned = secondaryRolesWithoutMainRole(current, nextMainRole || undefined);
    if (cleaned.length !== current.length) {
      form.setValue('secondaryRoles', cleaned, { shouldDirty: true });
    }
  };

  const emailChangeWarning = useEmailChangeWarning(form.control, member?.email);

  useClearOnChange({
    form,
    watch: 'classification',
    clearFields: { department: '', mainRole: '', secondaryRoles: [] },
    shouldClear: (next) => next === 'external',
    initial: memberClassification,
    isOpen
  });

  return (
    <div className="flex flex-col gap-8">
      {/* Basic Information Section */}
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormInput
            control={form.control}
            name="firstName"
            label={t('common.FirstName')}
            placeholder={t('organization.members.firstNamePlaceholder')}
            required
          />

          <FormInput
            control={form.control}
            name="lastName"
            label={t('common.LastName')}
            placeholder={t('organization.members.lastNamePlaceholder')}
            required
          />
        </div>

        <FormInput
          control={form.control}
          name="email"
          label={t('common.Email')}
          placeholder={t('organization.members.emailPlaceholder')}
          type="email"
          required
          description={emailChangeWarning}
        />

        <FormLocationSelect
          control={form.control}
          name="location"
          label={t('organization.members.location')}
          placeholder={t('organization.members.selectLocation')}
        />
      </div>

      {/* Classification Section - Only shown on user management page */}
      {showUserTypes && (
        <div className="flex flex-col gap-4">
          <FieldSeparator>{t('common.Classification')}</FieldSeparator>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormSelect
              control={form.control}
              name="classification"
              label={t('common.Classification')}
              placeholder={t('organization.members.selectClassification')}
              options={classificationOptions}
              onValueChange={(next) => {
                const defaults = externalDefaultUserTypes(next, form.getValues('userTypes'));
                if (defaults) form.setValue('userTypes', defaults, { shouldDirty: true });
              }}
              required
            />

            {isExternal && (
              <FormInput
                control={form.control}
                name="externalOrganization"
                label={t('organization.members.externalOrganization')}
                placeholder={t('organization.members.externalOrgPlaceholder')}
                required
              />
            )}
          </div>
        </div>
      )}

      {/* Department & OrgFunction Section - Only shown for internal members */}
      {!isExternal && (
        <div className="flex flex-col gap-4">
          <FieldSeparator>{t('organization.members.relationships')}</FieldSeparator>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormDepartmentSelect
              control={form.control}
              name="department"
              label={t('common.entityTypes.Department')}
              placeholder={t('organization.members.selectDepartment')}
              required
              disabled={!!lockedDepartmentId}
            />

            <FormSelect
              control={form.control}
              name="mainRole"
              label={t('organization.members.mainRole')}
              placeholder={
                orgFunctionsLoading
                  ? t('orgFunctionSelect.loading')
                  : t('organization.members.selectMainRole')
              }
              options={roleOrgFunctionOptions}
              onValueChange={handleMainRoleChange}
              allowClear
              searchable
              searchPlaceholder={t('orgFunctionSelect.search')}
              disabled={orgFunctionsLoading}
            />
          </div>

          <FormMultiSelect
            control={form.control}
            name="secondaryRoles"
            label={t('organization.members.secondaryRoles')}
            placeholder={
              orgFunctionsLoading
                ? t('orgFunctionSelect.loading')
                : t('organization.members.selectSecondaryRoles')
            }
            items={secondaryRoleOptions}
            searchable
            searchPlaceholder={t('orgFunctionSelect.search')}
            showSelectedTags
            disabled={orgFunctionsLoading}
          />
        </div>
      )}

      {/* Access & Permissions Section */}
      {(isEditing || showUserTypes) && (
        <div className="flex flex-col gap-4">
          <FieldSeparator>{t('organization.members.accessPermissions')}</FieldSeparator>

          {!isEditing ? (
            <FormMultiSelect
              control={form.control}
              name="userTypes"
              label={t('common.UserTypes')}
              placeholder={t('organization.members.selectUserTypes')}
              items={userTypeOptions}
              selectedTextFormatter={(count) => t('common.itemsSelected', { count })}
            />
          ) : (
            <>
              {showUserTypes ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_minmax(0,0.43fr)]">
                  <FormMultiSelect
                    control={form.control}
                    name="userTypes"
                    label={t('common.UserTypes')}
                    placeholder={t('organization.members.selectUserTypes')}
                    items={userTypeOptions}
                    selectedTextFormatter={(count) => t('common.itemsSelected', { count })}
                  />

                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium">{t('common.Status')}</label>
                    <Badge
                      colorScheme={getUserStatusColorScheme(member!.status)}
                      className="h-10 px-3 text-sm flex items-center justify-center w-full"
                    >
                      {getUserStatusLabel(member!.status, t)}
                    </Badge>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">{t('common.Status')}</label>
                  <Badge
                    colorScheme={getUserStatusColorScheme(member!.status)}
                    className="h-10 px-3 text-sm flex items-center justify-center w-full max-w-32"
                  >
                    {getUserStatusLabel(member!.status, t)}
                  </Badge>
                </div>
              )}

              {!showUserTypes && isExternal && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormInput
                    control={form.control}
                    name="externalOrganization"
                    label={t('organization.members.externalOrganization')}
                    placeholder={t('organization.members.externalOrgPlaceholder')}
                    required
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Send Invitation Action - Only shown in create mode */}
      {!isEditing && (
        <div className="flex flex-col gap-4">
          <FieldSeparator>{t('organization.members.statusActions')}</FieldSeparator>
          <label className="relative w-full rounded-lg border p-4 bg-background hover:bg-accent/5 transition-colors cursor-pointer block">
            <FormCheckbox
              control={form.control}
              name="sendInvitation"
              label={t('organization.members.sendInvitation')}
              description={t('organization.members.sendInvitationHelper')}
            />
          </label>
        </div>
      )}

      {/* Status Actions - Only shown in edit mode */}
      {isEditing && member && (
        <OrgMemberStatusActions
          member={member}
          onSendInvitation={onSendInvitation}
          onUnsuspend={onUnsuspend}
          onSuspend={onSuspend}
          isSendingInvitation={isSendingInvitation}
          isUnsuspending={isUnsuspending}
          isSuspending={isSuspending}
        />
      )}
    </div>
  );
}
